import { FC, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useCompanyContext } from "Bands/contexts";
import { copy } from "shared/assets";
import { Card, IActionDropdownItem, Table, TableColumn, TableSkeleton } from "shared/components";
import { dataMetaSelector, disptachGetCustomSubscriptionRequests } from "Bands/state";
import { BAND_ACTIONS, IBandCustomSubscriptionRequest } from "Bands/types";
import { IAPIState, IStatus, ITheme } from "shared/types";
import { Clock } from "shared/assets/icons";
import { CancelRequestModal } from "./CancelRequestModal";
import { addTemporaryToast } from "shared/components/Toast";

const subscriptionsCopy = copy.companyDashboard.subscriptions;
const customCopy = copy.subscription.custom;

export const SubscriptionRequestsTable: FC = () => {
  const [showDeleteModal, setShowDeleteModal] = useState<IBandCustomSubscriptionRequest | null>(
    null
  );

  const { api: apiMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.getCustomSubscriptionRequests)
  );

  const { companySubscriptionRequests, companyPath } = useCompanyContext();
  const dispatch = useDispatch();

  useEffect(() => {
    if (companyPath) {
      disptachGetCustomSubscriptionRequests({ dispatch, company: companyPath });
    }
  }, [companyPath, dispatch]);

  // delete request
  const { api: deleteApiMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.deleteCustomSubscriptionRequest)
  );
  useEffect(() => {
    if (deleteApiMeta?.status === IAPIState.succeeded) {
      setShowDeleteModal(null);

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: customCopy.messages.canceled,
        })
      );
    }
  }, [dispatch, deleteApiMeta]);

  const getRowActions = useCallback(
    (subscriptionId: string): IActionDropdownItem[] => {
      const request = companySubscriptionRequests.find(({ id }) => id === subscriptionId);

      if (!request) return [];

      return [
        {
          label: copy.form.cancel,
          handleClick: () => setShowDeleteModal(request),
          status: IStatus.error,
        },
      ];
    },
    [companySubscriptionRequests]
  );

  if (apiMeta?.status === IAPIState.pending) return <TableSkeleton theme={ITheme.tertiary} />;

  if (!companySubscriptionRequests?.length) return null;

  return (
    <>
      <Card icon={Clock} title={copy.companyDashboard.subscriptionRequests.title}>
        <Table
          columns={columns}
          data={companySubscriptionRequests}
          emptyCopy={subscriptionsCopy.empty}
          getRowActions={getRowActions}
        />
      </Card>

      {showDeleteModal && (
        <CancelRequestModal
          request={showDeleteModal}
          handleClose={() => setShowDeleteModal(null)}
        />
      )}
    </>
  );
};

const columns: TableColumn[] = [
  { Header: subscriptionsCopy.columns.band, accessor: "bands[0].title" },
  {
    Header: subscriptionsCopy.columns.by,
    accessor: "createdBy",
    Cell: ({ value }) => `${value.firstname} ${value.lastname}`,
  },
];
